import { ConfigProvider, Tabs, Tooltip } from 'antd'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import {
  DamageMechanism,
  NiiStrategy,
  ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { ExternalLink, GripHorizontal, X } from 'lucide-react'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { DamageMechanismsPanel } from './DamageMechanismsPanel'
import { DesignFunctionPanel } from './DesignFunctionPanel'
import { NiiStrategyPanel } from './NiiStrategyPanel'
import { RbaInformationPanel } from './RbaInformationPanel'
import { ZoneDescriptionPanel } from './ZoneDescriptionPanel'
import { ZoneSummaryCard } from './ZoneSummaryCard'

// Module-level counters shared across all open cards
let cardOpenCount = 0
let topZIndex = 1100
const RIGHT_MARGIN = 16
const CASCADE_STEP = 40
const MIN_WIDTH = 480
const MIN_HEIGHT = 300
const DEFAULT_WIDTH = 760
const TOP_OFFSET = 64 // clear top nav bar

const FloatingWrapper = styled.div<{
  $x: number
  $y: number
  $width: number
  $height: number
  $zIndex: number
  $isInteracting: boolean
}>`
  position: fixed;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  z-index: ${({ $zIndex }) => $zIndex};
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.4),
    0 4px 16px rgba(0, 0, 0, 0.25);
  background: ${({ theme }) => theme.forecast.background1};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  overflow: hidden;
  cursor: ${({ $isInteracting }) => ($isInteracting ? 'grabbing' : 'default')};
  user-select: ${({ $isInteracting }) => ($isInteracting ? 'none' : 'auto')};
`

const TitleBar = styled.div<{ $zoneColor: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.forecast.background2};
  border-bottom: 2px solid ${({ $zoneColor }) => $zoneColor};
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`

const DragHandle = styled.div`
  color: ${({ theme }) => theme.forecast.tertiaryText};
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const ZoneColorDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`

const TitleText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.forecast.secondaryText};
  flex-shrink: 0;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const NavigateButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
    color: ${({ theme }) => theme.forecast.defaultText};
    border-color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const CardBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`

const TabsContainer = styled.div`
  flex: 1;

  .ant-tabs-tabpane {
    padding: 12px 0 0 0;
  }
`

const ResizeHandle = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 3px;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  z-index: 1;

  /* Diagonal lines mimicking a resize grip */
  background-image: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 2px,
    ${({ theme }) => theme.forecast.tertiaryText} 2px,
    ${({ theme }) => theme.forecast.tertiaryText} 3px
  );
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`

export interface ZoneFloatingCardProps {
  zone: ZoneParams
  onClose: () => void
  onSave?: (updates: Partial<ZoneParams>) => void
  readonly?: boolean
}

type DragMode = 'move' | 'resize' | null

export const ZoneFloatingCard: FC<ZoneFloatingCardProps> = ({
  zone,
  onClose,
  onSave,
  readonly,
}) => {
  const { selectedDocumentId, setSelectedDocumentId } = useLibraDocument()

  const isOnZoneDocument = selectedDocumentId === zone.zone_reference_image_id

  const [pos, setPos] = useState(() => {
    const idx = cardOpenCount % 5
    cardOpenCount++
    return {
      x: window.innerWidth - DEFAULT_WIDTH - RIGHT_MARGIN - idx * CASCADE_STEP,
      y: TOP_OFFSET,
    }
  })

  const [size, setSize] = useState(() => ({
    width: DEFAULT_WIDTH,
    height: window.innerHeight - TOP_OFFSET,
  }))

  // Each card gets its own z-index; clicking/dragging brings it to the top
  const [zIndex, setZIndex] = useState(() => ++topZIndex)

  const bringToFront = useCallback(() => {
    setZIndex((current) => {
      if (current === topZIndex) return current // already on top
      return ++topZIndex
    })
  }, [])

  // Decrement counter when card unmounts
  useEffect(() => {
    return () => {
      cardOpenCount = Math.max(0, cardOpenCount - 1)
    }
  }, [])

  // Keep refs in sync with state so drag callbacks never need pos/size as deps
  const posRef = useRef(pos)
  const sizeRef = useRef(size)
  useEffect(() => {
    posRef.current = pos
  }, [pos])
  useEffect(() => {
    sizeRef.current = size
  }, [size])

  const [dragMode, setDragMode] = useState<DragMode>(null)
  const interactStartRef = useRef<{
    mouseX: number
    mouseY: number
    cardX: number
    cardY: number
    width: number
    height: number
  } | null>(null)

  // --- Move drag ---
  const handleTitleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setDragMode('move')
    interactStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      cardX: posRef.current.x,
      cardY: posRef.current.y,
      width: sizeRef.current.width,
      height: sizeRef.current.height,
    }
  }, [])

  // --- Resize drag ---
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragMode('resize')
    interactStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      cardX: posRef.current.x,
      cardY: posRef.current.y,
      width: sizeRef.current.width,
      height: sizeRef.current.height,
    }
  }, [])

  // Unified mousemove / mouseup handler
  useEffect(() => {
    if (!dragMode) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!interactStartRef.current) return
      const dx = e.clientX - interactStartRef.current.mouseX
      const dy = e.clientY - interactStartRef.current.mouseY

      if (dragMode === 'move') {
        const newX = interactStartRef.current.cardX + dx
        const newY = interactStartRef.current.cardY + dy
        setPos({
          x: Math.min(
            window.innerWidth - sizeRef.current.width,
            Math.max(0, newX)
          ),
          y: Math.min(
            window.innerHeight - sizeRef.current.height,
            Math.max(0, newY)
          ),
        })
      } else if (dragMode === 'resize') {
        setSize({
          width: Math.max(MIN_WIDTH, interactStartRef.current.width + dx),
          height: Math.max(MIN_HEIGHT, interactStartRef.current.height + dy),
        })
      }
    }

    const handleMouseUp = () => {
      setDragMode(null)
      interactStartRef.current = null
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragMode])

  const handleUpdate = useCallback(
    (updates: Partial<ZoneParams>) => onSave?.(updates),
    [onSave]
  )

  const handleDamageMechanismsChange = useCallback(
    (mechanisms: DamageMechanism[]) => {
      handleUpdate({ damage_mechanisms: mechanisms })
    },
    [handleUpdate]
  )

  const handleStrategyChange = useCallback(
    (strategy: NiiStrategy) => {
      handleUpdate({ nii_strategy: strategy })
    },
    [handleUpdate]
  )

  const popupTheme = useMemo(
    () => ({ token: { zIndexPopupBase: zIndex + 1 } }),
    [zIndex]
  )

  const zoneColor = zone.color || '#3B82F6'

  const tabItems = useMemo(
    () => [
      {
        key: 'basic',
        label: 'Basic Info',
        children: (
          <ZoneDescriptionPanel
            zone={zone}
            onChange={handleUpdate}
            readonly={readonly}
          />
        ),
      },
      {
        key: 'design',
        label: 'Design & Function',
        children: (
          <DesignFunctionPanel
            zone={zone}
            onChange={handleUpdate}
            readonly={readonly}
          />
        ),
      },
      {
        key: 'damage',
        label: 'Damage Mechanisms',
        children: (
          <DamageMechanismsPanel
            mechanisms={zone.damage_mechanisms ?? []}
            onChange={handleDamageMechanismsChange}
            readonly={readonly}
            zoneId={zone.id}
          />
        ),
      },
      {
        key: 'strategy',
        label: 'NII Strategy',
        children: (
          <NiiStrategyPanel
            strategy={zone.nii_strategy}
            onChange={handleStrategyChange}
            readonly={readonly}
            zoneId={zone.id}
          />
        ),
      },
      {
        key: 'rba',
        label: 'Risk Assessment',
        children: (
          <RbaInformationPanel
            zone={zone}
            onChange={handleUpdate}
            readonly={readonly}
          />
        ),
      },
    ],
    [
      zone,
      handleUpdate,
      handleDamageMechanismsChange,
      handleStrategyChange,
      readonly,
    ]
  )

  return createPortal(
    <ConfigProvider getPopupContainer={() => document.body} theme={popupTheme}>
      <FloatingWrapper
        $x={pos.x}
        $y={pos.y}
        $width={size.width}
        $height={size.height}
        $zIndex={zIndex}
        $isInteracting={dragMode !== null}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={bringToFront}>
        <TitleBar $zoneColor={zoneColor} onMouseDown={handleTitleMouseDown}>
          <DragHandle>
            <GripHorizontal size={14} />
          </DragHandle>
          <ZoneColorDot $color={zoneColor} />
          <TitleText>
            {readonly ? 'Zone Details' : 'Zone Analysis'}: {zone.title}
          </TitleText>
          {!isOnZoneDocument && zone.zone_reference_image_id && (
            <Tooltip title="Navigate to this zone's reference image">
              <NavigateButton
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedDocumentId(zone.zone_reference_image_id!)
                }}
                onMouseDown={(e) => e.stopPropagation()}>
                <ExternalLink size={12} />
                Go to Document
              </NavigateButton>
            </Tooltip>
          )}
          <CloseButton
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
            title='Close'>
            <X size={16} />
          </CloseButton>
        </TitleBar>

        <CardBody>
          <ZoneSummaryCard zone={zone} />
          <TabsContainer>
            <Tabs items={tabItems} size='small' />
          </TabsContainer>
        </CardBody>

        <ResizeHandle onMouseDown={handleResizeMouseDown} title='Resize' />
      </FloatingWrapper>
    </ConfigProvider>,
    document.body
  )
}
